import type { IServiceStartingPoint } from '@common/interfaces';
import type { ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import { CustomControlCode, ServiceCode } from '@common/enums';
import { never } from '@common/utils/never';
import type { IServiceDataItem } from './getServiceData';

export type cardioServiceCodeType =
    | ServiceCode.TCOH
    | ServiceCode.TCOR
    | ServiceCode.TIECG
    | ServiceCode.TCCh
    | ServiceCode.TCCr
    | ServiceCode.TCCi
    | ServiceCode.Holter;

export function getServiceDataForEpisodeCardiology(
    service: ServiceForSelectedServiceType,
    serviceCode: cardioServiceCodeType,
    startingPoint?: IServiceStartingPoint
): Partial<IServiceDataItem> {
    switch (serviceCode) {
        case ServiceCode.TCOH:
        case ServiceCode.Holter:
            if (
                service.isServiceProvider ||
                startingPoint?.activities[0].customControlCode === CustomControlCode.HolterDirectExaminationRequest
            ) {
                return {
                    costs: 'eigen risico belast',
                    order: 1,
                    steps: [
                        'Stel de holter in op 1, 2, 3 of 7 dagen (event) en sluit deze aan bij de patiënt',
                        'Een Ksyos cardioloog beoordeelt het onderzoek',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Vraag optioneel een consult aan bij een (regionale) cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                        </>
                    ),
                    title: 'Holter/event onderzoek (op praktijk)',
                    waitingTime: 'start direct, uitslag 3 werkdagen na uitlezen',
                };
            } else if (
                service.isExternalHolter ||
                startingPoint?.activities[0].customControlCode === CustomControlCode.HolterExternalExaminationRequest
            ) {
                return {
                    costs: 'eigen risico belast',
                    order: 3,
                    steps: [
                        'Verwijs de patiënt naar diagnostisch centrum, waar ze de holter instellen en aansluiten',
                        'Een Ksyos cardioloog beoordeelt het onderzoek',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Vraag optioneel een consult aan bij een (regionale) cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                        </>
                    ),
                    title: 'Holter/event onderzoek (extern)',
                    waitingTime: 'na afspraak met locatie, uitslag 3 werkdagen na uitlezen',
                };
            } else if (startingPoint?.activities[0].customControlCode === CustomControlCode.HolterConsultRequest) {
                return {
                    costs: 'eigen risicovrij (m.u.v. DSW)',
                    order: 1,
                    steps: [
                        'Stel je vraag aan een regionale of Ksyos cardioloog via Ksyos platform',
                        'Een cardioloog beantwoordt de vraag digitaal',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Stel eventueel aanvullende vraag aan cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor aanvullend advies over <strong>vervolgbeleid na een holteronderzoek</strong>
                        </>
                    ),
                    title: 'Holter consult',
                    waitingTime: '2 werkdagen',
                };
            } else {
                // HolterHomeExaminationRequest || !startingPoint
                return {
                    costs: 'eigen risico belast',
                    order: 2,
                    steps: [
                        'Ksyos stelt de holter in op 1, 2, 3 of 7 dagen (event) en stuurt deze naar de patiënt',
                        'Een Ksyos cardioloog beoordeelt het onderzoek',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Vraag optioneel een consult aan bij een (regionale) cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                        </>
                    ),
                    title: 'Holter/event onderzoek (thuis bij patient)',
                    waitingTime: 'start binnen ca. 5 werkdagen, uitslag 3 werkdagen na uitlezen',
                };
            }
        case ServiceCode.TCOR:
            if (service.isServiceProvider) {
                return {
                    costs: 'eigen risicovrij',
                    order: 4,
                    steps: [
                        'Maak de rust ECG bij de patiënt',
                        'Beoordeel de rust ECG zelf',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Vraag optioneel een consult aan bij een (regionale) cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                        </>
                    ),
                    title: 'Rust ECG onderzoek (op praktijk)',
                    waitingTime: 'binnen 3 werkdagen',
                };
            } else {
                return {
                    costs: 'eigen risico belast',
                    order: 5,
                    steps: [
                        'Verwijs de patiënt naar diagnostisch centrum voor het maken van een rust ECG',
                        'Een regionale of Ksyos cardioloog beoordeelt het onderzoek',
                        'Je ontvangt de uitslag via Edifact (in je HIS)',
                        'Vraag optioneel een consult aan bij een (regionale) cardioloog',
                    ],
                    subTitle: (
                        <>
                            Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                        </>
                    ),
                    title: 'Rust ECG onderzoek (extern)',
                    waitingTime: 'afhankelijk van afspraak locatie',
                };
            }

        case ServiceCode.TIECG:
            return {
                costs: '',
                order: 6,
                subTitle: (
                    <>
                        Voor diagnostiek bij <strong>hartritmestoornissen</strong>
                    </>
                ),
                title: 'Inspannings ECG',
                waitingTime: '',
            };

        case ServiceCode.TCCh:
            return {
                costs: 'eigen risicovrij (m.u.v. DSW)',
                order: 1,
                steps: [
                    'Stel je vraag aan een regionale of Ksyos cardioloog via Ksyos platform',
                    'Een cardioloog beantwoordt de vraag digitaal',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                    'Stel eventueel aanvullende vraag aan cardioloog',
                ],
                subTitle: (
                    <>
                        Voor aanvullend advies over <strong>vervolgbeleid na een holteronderzoek</strong>
                    </>
                ),
                title: 'Consult holteronderzoek',
                waitingTime: '2 werkdagen',
            };

        case ServiceCode.TCCr:
            return {
                costs: 'eigen risicovrij (m.u.v. DSW)',
                order: 2,
                steps: [
                    'Stel je vraag aan een regionale of Ksyos cardioloog via Ksyos platform',
                    'Een cardioloog beantwoordt de vraag digitaal',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                    'Stel eventueel aanvullende vraag aan cardioloog',
                ],
                subTitle: (
                    <>
                        Voor advies over <strong>vervolgbeleid na een rust ECG onderzoek</strong>
                    </>
                ),
                title: 'Consult rust ECG',
                waitingTime: '2 werkdagen',
            };
        case ServiceCode.TCCi:
            return {
                costs: '',
                order: 3,
                subTitle: (
                    <>
                        Voor advies over <strong>vervolgbeleid na een inspannings ECG onderzoek</strong>
                    </>
                ),
                title: 'Consult inspannings ECG',
                waitingTime: '',
            };

        default:
            never(serviceCode);
    }
}
